import React, { useState, useEffect, useRef } from 'react';
import { housesData } from '../data';
import { useParams } from 'react-router-dom';
import { BiBed, BiBath, BiArea } from 'react-icons/bi';
import { RiMapPinLine, RiHome5Line } from 'react-icons/ri';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import '../styles/PropertyDetails.css';
import Footer from '../components/Footer';
import { gsap } from 'gsap';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, useScroll, useTransform } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);


const PropertyDetails = ({ toggleMobileMenu }) => {
  const { id } = useParams();
  const [showNewsletter, setShowNewsletter] = useState(true);
  const [memberCount, setMemberCount] = useState(68199);
  const targetCount = 70000;
  const houseDetailsRef = useRef(null);
  const textLinesRef = useRef([]);
  const memberCountRef = useRef(null); // Referință pentru elementul memberCount
  const house = housesData.find((house) => house.id === parseInt(id));
  const scrollRef = useRef(null);

  const imageRef = useRef(null); 


    useEffect(() => {
      window.scrollTo(0, 0); 
    }, []);


  useEffect(() => {
    const countNode = memberCountRef.current; // captured for the cleanup below
    const handleIntersection = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          let startCount = memberCount;
          const updateCount = () => {
            const increment = Math.ceil((targetCount - startCount) / 100);
            startCount += increment;
            if (startCount <= targetCount) {
              setMemberCount(startCount);
              requestAnimationFrame(updateCount);
            } else {
              setMemberCount(targetCount);
            }
          };
          updateCount();
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    });

    if (countNode) {
      observer.observe(countNode);
    }

    return () => {
      if (countNode) {
        observer.unobserve(countNode);
      }
    };
  }, [memberCount, targetCount]);

  useEffect(() => {
    setShowNewsletter(true);
    if (houseDetailsRef.current) {
      houseDetailsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    gsap.fromTo(
      textLinesRef.current,
      { y: 10, opacity: 0, skewY: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.4,
        ease: 'power4.out',
        stagger: { amount: 0.4 },
        delay: 0.8,
      }
    );
  }, []);

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Mișcare pe axa Y, dar în mod controlat
  const y = useTransform(scrollYProgress, [0, 1], ["-40%", "80%"]); // Ajustează cât de mult să se miște imaginea

  if (!house) {
    return (
      <div className="container mx-auto text-center mt-8">
        <h2 className="text-2xl font-semibold">House Not Found</h2>
        <p>The requested house does not exist.</p>
      </div>
    );
  }


  return (
    <div ref={scrollRef}>
      <>
        <section className="house-details" ref={houseDetailsRef}>
          <Header toggleMobileMenu={toggleMobileMenu} />
          <div className="container mt-header-spacing  mx-auto  xl:min-h-[900px] lg:min-h-[900px] md:min-h-[600px] sm:min-h-[500px] xl:mb-2 lg:mb-2 md:mb-4 sm:mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl text-white font-semibold" ref={(el) => textLinesRef.current.push(el)}>{house.name}</h2>
                <h3 className="text-lg text-white mb-4" ref={(el) => textLinesRef.current.push(el)}>{house.address}</h3>
              </div>
            </div>

            <div className="flex flex-col items-start gap-8 lg:flex-row">

              <div className="w-full md:max-w-[768px]">
                <div className="mb-8 mt-4 md:mt-0 md:w-full h-[450px] md:px-0 px-8 md:mr-0 mr-2 relative overflow-hidden responsive-image-container">
                <motion.div 
                  className='w-full house-img overflow-x-hidden'
                  style={{ y, }}>
                  <img className='w-full object-cover ' ref={imageRef} src={house.image} alt="house_image" />
                </motion.div>
              </div>

                <div className="flex">
                <div className="flex-col">
                  <div className="house-c-t mb-4 lg:mb-3 flex gap-x-[1.30rem] text-sm">
                    <div className="text-white flex items-center" ref={(el) => textLinesRef.current.push(el)}>
                      <RiHome5Line className="text-indigo-700 text-2xl" style={{ marginRight: '5px' }} />
                      {house.type}
                    </div>
                    <div className="text-white flex items-center ml-8" ref={(el) => textLinesRef.current.push(el)}>
                      <RiMapPinLine className="text-indigo-700 text-2xl" style={{ marginRight: '5px' }} />
                      {house.country}
                    </div>
                  </div>    

                  <div className="house-dtls flex gap-x-6 mb-6">
                    <div className="flex gap-x-2 items-center" ref={(el) => textLinesRef.current.push(el)}>
                      <BiBed className="text-2xl text-indigo-700" />
                      <div className="text-white">{house.bedrooms}</div>
                    </div>
                    <div className="flex gap-x-2 items-center" ref={(el) => textLinesRef.current.push(el)}>
                      <BiBath className="text-2xl text-indigo-700" />
                      <div className="text-white">{house.bathrooms}</div>
                    </div>
                    <div className="flex gap-x-2 items-center" ref={(el) => textLinesRef.current.push(el)}>
                      <BiArea className="text-2x text-indigo-700" />
                      <div className="text-white">{house.surface}</div>
                    </div>
                  </div> 
                </div>

                <div className="text-3xl font-normal text-white ml-auto" ref={(el) => textLinesRef.current.push(el)}>${house.price}</div>                         
              </div>

                <div className="house-description md:leading-[1] leading-[0.9]" ref={(el) => textLinesRef.current.push(el)}><p>{house.description}</p></div>
              </div>

              <div className="contact-form flex-1 bg-transparent w-full mb-8 lg:backdrop-blur backdrop-blur border border-neutral-400 rounded-[0.5rem] px-6 py-8">
                <div className="flex item-center gap-x-4 mb-8">
                  <div className="w-20 h-20 border border-neutral-400 rounded-full agent-image-div">
                    <img src={house.agent.image} className="object-cover w-full h-full agent-img rounded-full" alt="agent-img" />
                  </div>
                  <div>
                    <div className="font-bold text-lg text-white" ref={(el) => textLinesRef.current.push(el)}>{house.agent.name}</div>
                    <Link to="" className="text-gray-500 text-sm">
                      View Listings
                    </Link>
                  </div>
                </div>

                <form className="flex flex-col gap-y-4">
                  <input
                    className="border-b border-neutral-400 focus:border-indigo-700 outline-none bg-transparent w-full px-4 h-14 text-sm text-gray-500"
                    type="text"
                    placeholder="Name*"
                  />
                  <input
                    className="border-b border-neutral-400 focus:border-indigo-700 outline-none bg-transparent w-full px-4 h-14 text-sm text-gray-500"
                    type="text"
                    placeholder="Email*"
                  />
                  <input
                    className="border-b border-neutral-400 focus:border-indigo-700 outline-none bg-transparent w-full px-4 h-14 text-sm text-gray-500"
                    type="text"
                    placeholder="Phone*"
                  />
                  <textarea
                    className="border-b border-neutral-400 focus:border-indigo-700 outline-none bg-transparent resize-none w-full p-2 h-36 text-sm text-gray-500"
                    placeholder="Message*"
                    defaultValue="Hi, I'm interested in this property and I want to schedule a visit."
                  />
                  <div className="gap-y-4 relative agent-actions">
                    <button className="send-message relative mb-4 text-white rounded-full p-4 text-sm w-full transition duration-500 border border-indigo-700 overflow-hidden group">
                      <p className="relative z-10 p-white !important">Send a message</p>
                      <span className="absolute inset-0 bg-indigo-700 transition-transform duration-500 transform group-hover:-translate-x-full"></span>
                    </button>
                    <div className="relative">
                    <button className="border border-indigo-700 bg-transparent p-4 w-full font-medium text-white 
              rounded-full backdrop-blur-xl transition-all duration-500 overflow-hidden
              before:absolute before:inset-0 before:bg-indigo-700 before:scale-x-0 before:origin-left
              before:transition-transform before:duration-500 before:z-[-1] hover:text-white
              hover:before:scale-x-100">  
                      <p className='p-white !important'>Call</p>
                    </button>
                  </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className='Newsltr'>
          {showNewsletter && (
            <div className="Newsletter-container flex flex-col text-white">
              <main className="container mx-auto px-4 flex-1 text-center">
                <div className="wrapper-container">

                  <h1 className='flex justify-center text-[2rem] leading-[0.9]'>Subscribe to our newsletter</h1>
                  <p className="text-base md:text-lg lg:text-2xl mb-8" ref={(el) => textLinesRef.current.push(el)}>
                    Get the latest real estate deals and exclusive updates delivered straight to your inbox.
                  </p>

                  {/* Button sits inside the input (own classes: nav.css forces
                      position:relative on .Recive-offers-button) */}
                  <form
                    id="revue-form"
                    name="revue-form"
                    target="_blank"
                    className="newsletter-form"
                  >
                    <label htmlFor="member_email" className="sr-only">Email address</label>
                    <div className="newsletter-field">
                      <input
                        placeholder="Your email address..."
                        type="email"
                        name="member[email]"
                        id="member_email"
                        autoComplete="email"
                        className="newsletter-input"
                      />
                      <button type="submit" className="newsletter-submit group">
                        <span className="relative z-10">Receive</span>
                        <span className="absolute inset-0 bg-indigo-700 transition-transform duration-500 transform group-hover:-translate-x-full"></span>
                      </button>
                    </div>
                  </form>

                  <div className="opacity-75 italic mt-8 newsletter-a">
                      By subscribing, you agree with Estateland{' '}
                      <a target="_blank" href="https://www.Estateland.com/terms-and-services/" rel="noreferrer" className="hover:opacity-80 duration-150">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a target="_blank" href="https://www.Estateland.com/privacy-policy/" rel="noreferrer" className="hover:opacity-80 duration-150">
                        Privacy Policy
                      </a>
                    </div>

                    <div className="font-black text-3xl md:text-2xl lg:text-3xl py-2 px-4 md:py-4 md:px-10 lg:py-2 lg:px-12 w-fit mx-auto mb-8 rounded-full" ref={memberCountRef}>
                    {memberCount.toLocaleString()} members
                    </div>
                </div>
              </main>
            </div>
          )}
        </section>
        <Footer />
      </>
    </div>
  );
};

export default PropertyDetails;